import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { UserApi } from "../../api/user";
import { YieldApi } from "../../api/yield";
import { baseTheme } from "mui-theme";
import { ThemeProvider } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning";
import SectionTitle from "../elements/sectionTitle";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import { ICreateUserRequest } from "models/api.models";
import handleEmailValidation from "App/utils/emailValidator";
import { store } from "redux/store";
import { setHotels } from "redux/Slice/hotelSlice";
import { ConfigApi } from "api/config";

interface Props {
  user: any;
  router: any;
  authData?: any;
  hotelData: {
    hotels: Array<{ hotelId: number; name: string; level: string }>;
  };
}

interface User {
  email: string;
  verificationStatus: boolean;
}

interface gnhStaff extends User {
  permissions: {
    gnh: number;
    notification: Array<number>;
  };
}

interface hotelStaff extends User {
  permissions: Array<{
    hotelId: number;
    permission: number;
    notification: boolean;
  }>;
}

interface State {
  isConfirmDeleteDialogOpen: boolean;
  userToDelete: string | null;
  gnhUsers: Array<gnhStaff>;
  hotelUsers: Array<hotelStaff>;
  expandedUser: string | null;
  editingPermission: number | null;
  editingNotifications: Array<number>;
  expandedHotelUser: string | null;
  editingHotelPermissions: { [hotelId: number]: number } | null;
  editingHotelNotifications: { [hotelId: number]: boolean } | null;
  gnhSearchQuery: string;
  selectedGnhPermissionFilter: number | "all";
  selectedGnhHotelFilter: number | "all";
  hotelSearchQuery: string;
  selectedHotelFilter: number | "all";
  showAddGnhUserDialog: boolean;
  newGnhUserEmail: string;
  newGnhUserPermission: number | "";
  newGnhUserNotifications: Array<number>;
  addGnhUserError: string | null;
  newHotelIdToAdd: number | "";
  newHotelPermissionToAdd: number | "";

  isAddHotelUserDialogOpen: boolean;
  newHotelUserEmail: string;
  newHotelUserPermissions: Array<{
    hotelId: number | "";
    permissionLevel: number | "";
    notificationsEnabled: boolean;
    hotelName?: string;
  }>;
  currentHotelSelectionForNewUser: {
    hotelId: number | "";
    permissionLevel: number | "";
    notificationsEnabled: boolean;
  };
  addHotelUserError: string | null;

  // Developer Settings
  isScheduleUpdateDialogOpen: boolean;
  scheduleUpdateEpochTime: string;
  scheduleUpdateError: string | null;

  // Developer: Delete Hotel
  isDeleteHotelDialogOpen: boolean;
  selectedHotelIdToDelete: number | "";
  isConfirmDeleteHotelOpen: boolean;
}

class UserDashboard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isConfirmDeleteDialogOpen: false,
      userToDelete: null,
      gnhUsers: [],
      hotelUsers: [],
      expandedUser: null,
      editingPermission: null,
      editingNotifications: [],
      expandedHotelUser: null,
      editingHotelPermissions: null,
      editingHotelNotifications: null,
      gnhSearchQuery: "",
      selectedGnhPermissionFilter: "all",
      selectedGnhHotelFilter: "all",
      hotelSearchQuery: "",
      selectedHotelFilter: "all",
      showAddGnhUserDialog: false,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      newGnhUserNotifications: [],
      addGnhUserError: null,
      newHotelIdToAdd: "",
      newHotelPermissionToAdd: "",
      isAddHotelUserDialogOpen: false,
      newHotelUserEmail: "",
      newHotelUserPermissions: [],
      currentHotelSelectionForNewUser: {
        hotelId: "",
        permissionLevel: 30,
        notificationsEnabled: false,
      },
      addHotelUserError: null,

      // Developer Settings
      isScheduleUpdateDialogOpen: false,
      scheduleUpdateEpochTime: "",
      scheduleUpdateError: null,

      // Developer: Delete Hotel
      isDeleteHotelDialogOpen: false,
      selectedHotelIdToDelete: "",
      isConfirmDeleteHotelOpen: false,
    };
  }

  async componentDidMount() {
    const res = await UserApi.getAllUsers();
    this.setState({
      gnhUsers: res.gnhStaff,
      hotelUsers: res.hotelStaff,
    });
  }

  getPermissionText(
    permission: number | { gnh: number; notification: Array<number> },
  ): string {
    if (typeof permission === "object" && permission.gnh !== undefined) {
      switch (permission.gnh) {
        case 60:
          return "Developer";
        case 50:
          return "GNH Systems Admin";
        case 40:
          return "GNH Revenue Manager";
        default:
          return `Unknown (${permission.gnh})`;
      }
    } else if (typeof permission === "number") {
      switch (permission) {
        case 50:
          return "GNH Systems Admin";
        case 40:
          return "GNH Revenue Manager";
        case 30:
          return "Pro Hotel Admin";
        case 20:
          return "Pro Hotel Revenue Manager";
        case 10:
          return "Assist Hotel Revenue Manager";
        case 5:
          return "Restricted User";
        default:
          return `Unknown (${permission})`;
      }
    }
    return "Unknown";
  }

  getHotelNameById = (hotelId: number): string => {
    const hotel = this.props.hotelData.hotels.find(
      (h) => h.hotelId === hotelId,
    );
    return hotel ? hotel.name : `ID: ${hotelId}`;
  };

  // Developer: Delete Hotel handlers (moved into class)
  handleOpenDeleteHotelDialog = () => {
    this.setState({
      isDeleteHotelDialogOpen: true,
      selectedHotelIdToDelete: "",
    });
  };

  handleCloseDeleteHotelDialog = () => {
    this.setState({ isDeleteHotelDialogOpen: false });
  };

  handleSelectHotelToDeleteChange = (event: SelectChangeEvent<number | "">) => {
    const value = event.target.value as number | "";
    this.setState({ selectedHotelIdToDelete: value });
  };

  handleDeleteHotelFromDialog = () => {
    const { selectedHotelIdToDelete } = this.state;
    if (selectedHotelIdToDelete === "") {
      toast.error("Please select a hotel to delete.");
      return;
    }
    this.setState({ isConfirmDeleteHotelOpen: true });
  };

  handleCancelConfirmDeleteHotel = () => {
    this.setState({ isConfirmDeleteHotelOpen: false });
  };

  handleConfirmDeleteHotel = async () => {
    const { selectedHotelIdToDelete } = this.state;
    const hotelIdNum = Number(selectedHotelIdToDelete);
    if (!hotelIdNum || Number.isNaN(hotelIdNum)) {
      toast.error("Invalid hotel selected.");
      return;
    }

    const apiCall = ConfigApi.deleteHotel(hotelIdNum);
    toast
      .promise(apiCall, {
        pending: `Deleting hotel ID ${hotelIdNum}...`,
        success: `Hotel ${hotelIdNum} deleted successfully`,
        error: "Failed to delete hotel.",
      })
      .then(() => {
        // Close dialogs and reset selection
        this.setState({
          isConfirmDeleteHotelOpen: false,
          isDeleteHotelDialogOpen: false,
          selectedHotelIdToDelete: "",
        });

        // Remove deleted hotel from Redux store so dropdown updates immediately
        const currentHotels = this.props.hotelData.hotels || [];
        const updatedHotels = currentHotels.filter(
          (h: { hotelId: number }) => h.hotelId !== hotelIdNum,
        );
        store.dispatch(setHotels(updatedHotels));

        // Then refetch from server to ensure Redux matches backend
        YieldApi.getUsersHotelsMeta()
          .then((fresh) => {
            if (Array.isArray(fresh)) {
              store.dispatch(setHotels(fresh));
            }
          })
          .catch((e) => {
            console.warn(
              "Failed to refetch hotels; keeping locally filtered list",
              e,
            );
          });
      });
  };

  renderDeleteHotelDialog = () => {
    const { isDeleteHotelDialogOpen, selectedHotelIdToDelete } = this.state;
    return (
      <Dialog
        open={isDeleteHotelDialogOpen}
        onClose={this.handleCloseDeleteHotelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Hotel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select the hotel you wish to delete. This action will require
            confirmation and is irreversible.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="delete-hotel-select-label">Hotel</InputLabel>
            <Select
              labelId="delete-hotel-select-label"
              value={selectedHotelIdToDelete}
              label="Hotel"
              onChange={this.handleSelectHotelToDeleteChange}
            >
              {this.props.hotelData.hotels.map((hotel) => (
                <MenuItem key={hotel.hotelId} value={hotel.hotelId}>
                  {hotel.name} (ID: {hotel.hotelId})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseDeleteHotelDialog}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={this.handleDeleteHotelFromDialog}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderConfirmDeleteHotelDialog = () => {
    const { isConfirmDeleteHotelOpen, selectedHotelIdToDelete } = this.state;
    const hotelName =
      typeof selectedHotelIdToDelete === "number"
        ? this.getHotelNameById(selectedHotelIdToDelete)
        : "";
    return (
      <Dialog
        open={isConfirmDeleteHotelOpen}
        onClose={this.handleCancelConfirmDeleteHotel}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{hotelName}</strong>
            {typeof selectedHotelIdToDelete === "number"
              ? ` (ID: ${selectedHotelIdToDelete})`
              : ""}
            ? This action is irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCancelConfirmDeleteHotel}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={this.handleConfirmDeleteHotel}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  handleGNHPasswordReset = (user: gnhStaff) => {
    let userRequest: ICreateUserRequest = {
      email: user.email,
      permissions: user.permissions,
    };
    toast.promise(UserApi.resetUserPassword(userRequest), {
      pending: "Resetting user password...",
      success: "User password reset successfully!",
      error: "Error resetting user password.",
    });
  };

  handleHotelPasswordReset = (user: hotelStaff) => {
    let userRequest: ICreateUserRequest = {
      email: user.email,
      permissions: user.permissions,
    };
    toast.promise(UserApi.resetUserPassword(userRequest), {
      pending: "Resetting user password...",
      success: "User password reset successfully!",
      error: "Error resetting user password.",
    });
  };

  handleEditUser = (user: gnhStaff) => {
    this.setState((prevState) => ({
      expandedUser: prevState.expandedUser === user.email ? null : user.email,
      editingPermission:
        prevState.expandedUser === user.email ? null : user.permissions.gnh,
      editingNotifications:
        prevState.expandedUser === user.email
          ? []
          : [...user.permissions.notification],
    }));
  };

  handlePermissionChange = (event: SelectChangeEvent<number>) => {
    this.setState({
      editingPermission: event.target.value as number,
    });
  };

  handleSavePermission = async (email: string) => {
    const { editingPermission, editingNotifications } = this.state;

    // Get current user email the same way as in renderGnhStaffTable
    let currentUserEmail = this.props.user?.username;
    if (!currentUserEmail) {
      try {
        const userDataString = localStorage.getItem("userData");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          currentUserEmail = userData?.username;
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    }

    const isCurrentUser =
      email.toLowerCase() === currentUserEmail?.toLowerCase();
    const isSystemAdmin = hasPermission(ACTIONS.MODIFY_GNHSTAFF);

    // Find the current user in gnhUsers to get their existing permissions
    const currentUser = this.state.gnhUsers.find(
      (user) => user.email.toLowerCase() === email.toLowerCase(),
    );

    if (!currentUser) {
      console.error("User not found:", email);
      toast.error("User not found");
      return;
    }

    let permissions:
      | { gnh: number; notification: number[] }
      | Array<{
          hotelId: number;
          permission: number;
          notification: boolean;
        }>;

    // If user is editing their own account and is not a system admin,
    // only allow them to update notification settings
    if (isCurrentUser && !isSystemAdmin) {
      permissions = {
        gnh: currentUser.permissions.gnh,
        notification: editingNotifications,
      };
    } else {
      // System admins can update everything for other users
      permissions = {
        gnh: editingPermission as number,
        notification: editingNotifications,
      };
    }

    const apiCall = UserApi.updateUser({
      email,
      permissions: permissions,
    });

    toast.promise(apiCall, {
      pending: isCurrentUser
        ? "Updating notification settings..."
        : "Updating GNH user permission...",
      success: isCurrentUser
        ? "Notification settings updated successfully!"
        : "GNH user permission updated successfully!",
      error: isCurrentUser
        ? "Error updating notification settings."
        : "Error updating GNH user permission.",
    });

    try {
      await apiCall;

      // Ensure we're only updating gnhUsers with the correct permissions type
      this.setState((prevState) => {
        // Only proceed with the update if permissions is a GNH permissions object
        if ("gnh" in permissions) {
          // We know permissions is the GNH type at this point, so we can safely cast it
          const gnhPermissions = permissions as {
            gnh: number;
            notification: number[];
          };

          return {
            gnhUsers: prevState.gnhUsers.map((user) =>
              user.email === email
                ? {
                    ...user,
                    permissions: {
                      gnh: gnhPermissions.gnh,
                      notification: gnhPermissions.notification,
                    },
                  }
                : user,
            ),
            expandedUser: null,
            editingPermission: null,
            editingNotifications: [],
          };
        }

        // If permissions is not the right type, just close the edit form without updating
        // but keep the existing gnhUsers to satisfy TypeScript
        return {
          gnhUsers: prevState.gnhUsers, // Keep existing users unchanged
          expandedUser: null,
          editingPermission: null,
          editingNotifications: [],
        };
      });
    } catch (error) {
      console.error("Failed to update user permission:", error);
    }
  };

  handleCancelEdit = () => {
    this.setState({
      expandedUser: null,
      editingPermission: null,
      editingNotifications: [],
      expandedHotelUser: null,
      editingHotelPermissions: null,
      editingHotelNotifications: null,
      newHotelIdToAdd: "",
      newHotelPermissionToAdd: "",
    });
  };

  handleEditHotelUser = (user: hotelStaff) => {
    const hotelPermissions: { [hotelId: number]: number } = {};
    const hotelNotifications: { [hotelId: number]: boolean } = {};

    user.permissions.forEach((hotel) => {
      hotelPermissions[hotel.hotelId] = hotel.permission;
      hotelNotifications[hotel.hotelId] = hotel.notification || false;
    });

    this.setState((prevState) => {
      const newEditingHotelPermissions =
        prevState.expandedHotelUser === user.email ? null : hotelPermissions;
      const newEditingHotelNotifications =
        prevState.expandedHotelUser === user.email ? null : hotelNotifications;
      return {
        expandedHotelUser:
          prevState.expandedHotelUser === user.email ? null : user.email,
        editingHotelPermissions: newEditingHotelPermissions,
        editingHotelNotifications: newEditingHotelNotifications,
      };
    });
  };

  handleHotelPermissionChange = (hotelId: number, value: number) => {
    this.setState((prevState) => ({
      editingHotelPermissions: {
        ...prevState.editingHotelPermissions,
        [hotelId]: value,
      },
    }));
  };

  toggleHotelNotification = (hotelId: number) => {
    this.setState((prevState) => ({
      editingHotelNotifications: {
        ...prevState.editingHotelNotifications,
        [hotelId]: !prevState.editingHotelNotifications?.[hotelId],
      },
    }));
  };

  handleHotelSelectChange = (event: SelectChangeEvent<number | "">) => {
    const { name, value } = event.target;
    this.setState(
      (prevState) =>
        ({
          ...prevState,
          [name]: value,
        }) as Pick<State, keyof State>,
    );
  };

  handleRemoveHotel = (hotelIdToRemove: number) => {
    this.setState((prevState) => {
      if (
        !prevState.editingHotelPermissions ||
        !prevState.editingHotelNotifications
      ) {
        return null;
      }
      const newPermissions = { ...prevState.editingHotelPermissions };
      const newNotifications = { ...prevState.editingHotelNotifications };
      delete newPermissions[hotelIdToRemove];
      delete newNotifications[hotelIdToRemove];
      return {
        editingHotelPermissions: newPermissions,
        editingHotelNotifications: newNotifications,
      };
    });
  };

  handleAddHotel = () => {
    const { newHotelIdToAdd, newHotelPermissionToAdd } = this.state;
    if (newHotelIdToAdd && newHotelPermissionToAdd) {
      this.setState((prevState) => ({
        editingHotelPermissions: {
          ...prevState.editingHotelPermissions,
          [newHotelIdToAdd]: newHotelPermissionToAdd as number,
        },
        editingHotelNotifications: {
          ...prevState.editingHotelNotifications,
          [newHotelIdToAdd]: false,
        },
        newHotelIdToAdd: "",
        newHotelPermissionToAdd: "",
      }));
    }
  };

  handleSaveHotelPermissions = async (email: string) => {
    const { editingHotelPermissions, editingHotelNotifications } = this.state;

    if (editingHotelPermissions && editingHotelNotifications) {
      const permissionsArray = Object.keys(editingHotelPermissions).map(
        (hotelIdStr) => {
          const hotelId = parseInt(hotelIdStr, 10);
          return {
            hotelId,
            permission: editingHotelPermissions[hotelId],
            notification: editingHotelNotifications[hotelId] || false,
          };
        },
      );

      const apiCall = UserApi.updateUser({
        email,
        permissions: permissionsArray,
      });

      toast.promise(apiCall, {
        pending: "Updating hotel user permissions...",
        success: "Hotel user permissions updated successfully!",
        error: "Error updating hotel user permissions.",
      });

      try {
        await apiCall;
        this.setState((prevState) => ({
          hotelUsers: prevState.hotelUsers.map((user) =>
            user.email === email
              ? { ...user, permissions: permissionsArray }
              : user,
          ),
          expandedHotelUser: null,
          editingHotelPermissions: null,
          editingHotelNotifications: null,
        }));
      } catch (error) {
        console.error("Failed to update hotel user permissions:", error);
      }
    }
  };

  handleDeleteUser = async (email: string) => {
    this.handleOpenConfirmDeleteDialog(email);
  };

  handleOpenConfirmDeleteDialog = (email: string) => {
    this.setState({ isConfirmDeleteDialogOpen: true, userToDelete: email });
  };

  handleCloseConfirmDeleteDialog = () => {
    this.setState({ isConfirmDeleteDialogOpen: false, userToDelete: null });
  };

  handleConfirmDelete = () => {
    if (!this.state.userToDelete) return;

    const email = this.state.userToDelete;
    console.log(`Confirmed deletion for user: ${email}`);

    const apiCall = UserApi.deleteUser({ email });

    toast.promise(apiCall, {
      pending: `Deleting user ${email}...`,
      success: `User ${email} deleted successfully!`,
      error: {
        render({ data }: any) {
          let errorMessage = `Failed to delete user ${email}.`;
          if (data?.response?.data?.message) {
            errorMessage = data.response.data.message;
          } else if (data?.message) {
            errorMessage = data.message;
          }
          return errorMessage;
        },
      },
    });

    apiCall
      .then(async () => {
        const res = await UserApi.getAllUsers();
        this.setState({
          gnhUsers: res.gnhStaff,
          hotelUsers: res.hotelStaff,
        });
      })
      .catch((error: any) => {
        console.error(`Failed to delete user ${email}:`, error);
      });

    this.handleCloseConfirmDeleteDialog();
  };

  handleGnhSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ gnhSearchQuery: event.target.value });
  };

  handleHotelSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ hotelSearchQuery: event.target.value });
  };

  handleHotelFilterChange = (event: SelectChangeEvent<any>) => {
    this.setState({ selectedHotelFilter: event.target.value });
  };

  handleGnhPermissionFilterChange = (event: SelectChangeEvent<any>) => {
    this.setState({ selectedGnhPermissionFilter: event.target.value });
  };

  clearGnhSearch = () => {
    this.setState({ gnhSearchQuery: "" });
  };

  clearHotelSearch = () => {
    this.setState({ hotelSearchQuery: "" });
  };
  handleOpenAddGnhUserDialog = () => {
    this.setState({
      showAddGnhUserDialog: true,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      newGnhUserNotifications: [],
      addGnhUserError: null,
    });
  };

  handleCloseAddGnhUserDialog = () => {
    this.setState({ showAddGnhUserDialog: false, addGnhUserError: null });
  };

  handleOpenAddHotelUserDialog = () => {
    this.setState({
      isAddHotelUserDialogOpen: true,
      newHotelUserEmail: "",
      newHotelUserPermissions: [],
      currentHotelSelectionForNewUser: {
        hotelId: "",
        permissionLevel: 30,
        notificationsEnabled: false,
      },
      addHotelUserError: null,
    });
  };

  handleCloseAddHotelUserDialog = () => {
    this.setState({ isAddHotelUserDialogOpen: false, addHotelUserError: null });
  };

  handleNewHotelUserInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    this.setState({ [name]: value } as any);
  };

  handleCurrentHotelSelectionChange = (event: SelectChangeEvent<any>) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      currentHotelSelectionForNewUser: {
        ...prevState.currentHotelSelectionForNewUser,
        [name]: value,
      },
    }));
  };

  handleToggleCurrentHotelNotification = () => {
    this.setState((prevState) => ({
      currentHotelSelectionForNewUser: {
        ...prevState.currentHotelSelectionForNewUser,
        notificationsEnabled:
          !prevState.currentHotelSelectionForNewUser.notificationsEnabled,
      },
    }));
  };

  handleAddHotelPermissionToDialogList = () => {
    const { currentHotelSelectionForNewUser, newHotelUserPermissions } =
      this.state;
    const { hotelData } = this.props;

    if (
      !currentHotelSelectionForNewUser.hotelId ||
      !currentHotelSelectionForNewUser.permissionLevel
    ) {
      this.setState({
        addHotelUserError: "Please select a hotel and permission level.",
      });
      return;
    }

    const hotel = hotelData.hotels.find(
      (h) => h.hotelId === currentHotelSelectionForNewUser.hotelId,
    );

    const newPermissionEntry = {
      ...currentHotelSelectionForNewUser,
      hotelName: hotel ? hotel.name : "Unknown Hotel",
    };

    this.setState((prevState) => ({
      newHotelUserPermissions: [
        ...prevState.newHotelUserPermissions,
        newPermissionEntry,
      ],
      currentHotelSelectionForNewUser: {
        hotelId: "",
        permissionLevel: 30,
        notificationsEnabled: false,
      },
      addHotelUserError: null,
    }));
  };

  handleSaveNewHotelUser = async () => {
    const { newHotelUserEmail, newHotelUserPermissions } = this.state;

    if (!newHotelUserEmail.trim()) {
      this.setState({ addHotelUserError: "Email address is required." });
      return;
    }

    if (!handleEmailValidation(newHotelUserEmail)) {
      this.setState({
        addHotelUserError: "Please enter a valid email address.",
      });
      return;
    }

    if (newHotelUserPermissions.length === 0) {
      this.setState({
        addHotelUserError: "At least one hotel permission must be configured.",
      });
      return;
    }

    const permissionsPayload = newHotelUserPermissions.map((p) => ({
      hotelId: p.hotelId as number,
      permission: p.permissionLevel as number,
      notification: p.notificationsEnabled,
    }));

    try {
      this.setState({ addHotelUserError: null });

      const apiCall = UserApi.createUser({
        email: newHotelUserEmail,
        permissions: permissionsPayload,
      });

      toast.promise(apiCall, {
        pending: "Creating hotel user...",
        success: "Hotel user created successfully!",
        error: {
          render({ data }: any) {
            let errorMessage = "Failed to create hotel user. ";
            if (data?.response?.data?.message) {
              errorMessage = data.response.data.message;
            } else if (data?.message) {
              errorMessage = data.message;
            }
            return errorMessage;
          },
        },
      });

      apiCall
        .then(async () => {
          const updatedUsers = await UserApi.getAllUsers();
          this.setState({
            gnhUsers: updatedUsers.gnhStaff,
            hotelUsers: updatedUsers.hotelStaff,
          });
          this.handleCloseAddHotelUserDialog();
        })
        .catch((error: any) => {
          console.error("Error creating hotel user:", error);
          let dialogErrorMessage = "Failed to create hotel user.";
          if (error?.response?.data?.message) {
            dialogErrorMessage = error.response.data.message;
          } else if (error?.message) {
            dialogErrorMessage = error.message;
          }
          this.setState({
            addHotelUserError: dialogErrorMessage,
          });
        });
    } catch (error: any) {
      console.error("Synchronous error in handleSaveNewHotelUser:", error);
      this.setState({
        addHotelUserError:
          error.message || "An unexpected synchronous error occurred.",
      });
    }
  };

  handleRemoveHotelPermissionFromDialogList = (indexToRemove: number) => {
    this.setState((prevState) => ({
      newHotelUserPermissions: prevState.newHotelUserPermissions.filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  handleNewGnhUserInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<number | "">,
  ) => {
    const target = event.target as
      HTMLInputElement | { name: string; value: unknown };
    const name = target.name;
    let value = target.value;

    if (name === "newGnhUserPermission" && value !== "") {
      value = Number(value);
    }

    this.setState(
      (prevState) =>
        ({
          ...prevState,
          [name]: value,
        }) as Pick<State, keyof State>,
    );
  };

  // Handler to open the Add GNH User dialog
  openAddGnhUserDialog = () => {
    this.setState({
      showAddGnhUserDialog: true,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      newGnhUserNotifications: [],
      addGnhUserError: null,
    });
  };

  // Handler to close the Add GNH User dialog
  closeAddGnhUserDialog = () => {
    this.setState({
      showAddGnhUserDialog: false,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      newGnhUserNotifications: [],
      addGnhUserError: null,
    });
  };

  // Developer Settings handlers
  handleOpenScheduleUpdateDialog = () => {
    this.setState({
      isScheduleUpdateDialogOpen: true,
      scheduleUpdateEpochTime: "",
      scheduleUpdateError: null,
    });
  };

  handleCloseScheduleUpdateDialog = () => {
    this.setState({
      isScheduleUpdateDialogOpen: false,
      scheduleUpdateEpochTime: "",
      scheduleUpdateError: null,
    });
  };

  handleScheduleUpdateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.setState({ scheduleUpdateEpochTime: event.target.value });
  };

  handleScheduleUpdate = async () => {
    const { scheduleUpdateEpochTime } = this.state;

    if (!scheduleUpdateEpochTime.trim()) {
      this.setState({ scheduleUpdateError: "Epoch time is required." });
      return;
    }

    const epochTime = parseInt(scheduleUpdateEpochTime.trim(), 10);
    if (isNaN(epochTime) || epochTime <= 0) {
      this.setState({
        scheduleUpdateError: "Please enter a valid epoch time.",
      });
      return;
    }

    try {
      this.setState({ scheduleUpdateError: null });

      const apiCall = YieldApi.developerNotify({ epochTime });

      toast.promise(apiCall, {
        pending: "Scheduling maintenance notification...",
        success: "Maintenance notification scheduled successfully!",
        error: {
          render({ data }: any) {
            let errorMessage = "Failed to schedule maintenance notification.";
            if (data?.response?.data?.message) {
              errorMessage = data.response.data.message;
            } else if (data?.message) {
              errorMessage = data.message;
            }
            return errorMessage;
          },
        },
      });

      await apiCall;
      this.handleCloseScheduleUpdateDialog();
    } catch (error: any) {
      console.error("Error scheduling maintenance notification:", error);
      let errorMessage = "Failed to schedule maintenance notification.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      this.setState({ scheduleUpdateError: errorMessage });
    }
  };

  handleSaveNewGnhUser = async () => {
    const { newGnhUserEmail, newGnhUserPermission, newGnhUserNotifications } =
      this.state;

    if (!newGnhUserEmail || newGnhUserPermission === "") {
      const errorMsg = "Email and permission are required.";
      this.setState({ addGnhUserError: errorMsg });
      toast.error(errorMsg);
      return;
    }

    if (!handleEmailValidation(newGnhUserEmail)) {
      const errorMsg = "Please enter a valid email address.";
      this.setState({ addGnhUserError: errorMsg });
      toast.error(errorMsg);
      return;
    }

    this.setState({ addGnhUserError: null });

    const permissions = {
      gnh: Number(newGnhUserPermission),
      notification: newGnhUserNotifications,
    };

    const apiCall = UserApi.createUser({
      email: newGnhUserEmail,
      permissions: permissions,
    });

    toast.promise(apiCall, {
      pending: "Creating new GNH user...",
      success: "GNH user created successfully!",
      error: {
        render({ data }: any) {
          let errorMessage = "Failed to create GNH user. Please try again.";
          if (data?.response?.data?.message) {
            errorMessage = data.response.data.message;
          } else if (data?.message) {
            errorMessage = data.message;
          }
          return errorMessage;
        },
      },
    });

    try {
      await apiCall;
      const res = await UserApi.getAllUsers();
      console.log("User API response:", res); // Debug log to see response structure
      this.setState({
        gnhUsers: res.gnhStaff,
        hotelUsers: res.hotelStaff,
      });
      this.closeAddGnhUserDialog();
    } catch (error: any) {
      console.error("Failed to create GNH user:", error);

      let errorMessage = "Failed to create GNH user. Please try again.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      this.setState({ addGnhUserError: errorMessage });
    }
  };

  renderAddGnhUserDialog = () => {
    const {
      showAddGnhUserDialog,
      newGnhUserEmail,
      newGnhUserPermission,
      newGnhUserNotifications,
      addGnhUserError,
    } = this.state;
    const { hotelData } = this.props;

    const handleNotificationChange = (hotelId: number) => {
      this.setState((prevState) => {
        const isSelected = prevState.newGnhUserNotifications.includes(hotelId);
        let updatedNotifications;

        if (isSelected) {
          // Remove hotel ID if already selected
          updatedNotifications = prevState.newGnhUserNotifications.filter(
            (id) => id !== hotelId,
          );
        } else {
          // Add hotel ID if not selected
          updatedNotifications = [
            ...prevState.newGnhUserNotifications,
            hotelId,
          ];
        }

        return { newGnhUserNotifications: updatedNotifications };
      });
    };

    return (
      <Dialog
        open={showAddGnhUserDialog}
        onClose={this.closeAddGnhUserDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New GNH Staff User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address, select the permission level, and choose
            hotels for notifications for the new GNH staff member.
          </DialogContentText>
          {addGnhUserError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {addGnhUserError}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="newGnhUserEmail"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={newGnhUserEmail}
            onChange={this.handleNewGnhUserInputChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
            <InputLabel id="new-gnh-user-permission-label">
              Permission Level
            </InputLabel>
            <Select
              labelId="new-gnh-user-permission-label"
              name="newGnhUserPermission"
              value={newGnhUserPermission}
              onChange={
                this.handleNewGnhUserInputChange as (
                  event: SelectChangeEvent<number | "">,
                  child: React.ReactNode,
                ) => void
              }
              label="Permission Level"
            >
              <MenuItem value="">
                <em>Select Permission</em>
              </MenuItem>
              <MenuItem value={50}>
                <Tooltip
                  title="Full system access and administrative privileges."
                  placement="right"
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    GNH Systems
                  </span>
                </Tooltip>
              </MenuItem>
              <MenuItem value={40}>
                <Tooltip
                  title="Access to revenue management features and reporting."
                  placement="right"
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    GNH Revenue Managers
                  </span>
                </Tooltip>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Hotels for Notifications</InputLabel>
            <Select
              multiple
              value={newGnhUserNotifications}
              onChange={(event) => {
                const value = event.target.value as number[];
                this.setState({ newGnhUserNotifications: value });
              }}
              label="Select Hotels for Notifications"
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>No hotels selected</em>;
                }
                if (selected.length === 1) {
                  const hotel = hotelData.hotels.find(
                    (h) => h.hotelId === selected[0],
                  );
                  return hotel ? hotel.name : "Unknown Hotel";
                }
                return `${selected.length} hotels selected`;
              }}
            >
              {hotelData.hotels.map((hotel) => (
                <MenuItem key={hotel.hotelId} value={hotel.hotelId}>
                  <Checkbox
                    checked={newGnhUserNotifications.includes(hotel.hotelId)}
                  />
                  {hotel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseAddGnhUserDialog}>Cancel</Button>
          <Button onClick={this.handleSaveNewGnhUser} variant="contained">
            Save User
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderAddHotelUserDialog = () => {
    const {
      isAddHotelUserDialogOpen,
      newHotelUserEmail,
      newHotelUserPermissions,
      currentHotelSelectionForNewUser,
      addHotelUserError,
    } = this.state;
    const { hotelData } = this.props;

    const availableHotels = hotelData.hotels.filter(
      (h) => !newHotelUserPermissions.some((p) => p.hotelId === h.hotelId),
    );

    const getPermissionName = (level: number | "") => {
      if (level === 30) return "Pro Admin";
      if (level === 10) return "Revanista User";
      if (level === 5) return "Restricted User";
      return "Unknown";
    };

    return (
      <Dialog
        open={isAddHotelUserDialogOpen}
        onClose={this.handleCloseAddHotelUserDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Hotel User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Enter the user&apos;s email address and configure their hotel access
            permissions below.
          </DialogContentText>

          {addHotelUserError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {addHotelUserError}
            </Typography>
          )}

          <TextField
            autoFocus
            margin="dense"
            name="newHotelUserEmail" // Added name for handler
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={newHotelUserEmail}
            onChange={this.handleNewHotelUserInputChange}
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Add Hotel Access
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <FormControl variant="standard" sx={{ minWidth: 200, flexGrow: 1 }}>
              <InputLabel id="select-hotel-label">Hotel</InputLabel>
              <Select
                labelId="select-hotel-label"
                name="hotelId"
                value={currentHotelSelectionForNewUser.hotelId}
                onChange={this.handleCurrentHotelSelectionChange}
                label="Hotel"
              >
                <MenuItem value="">
                  <em>Select Hotel</em>
                </MenuItem>
                {availableHotels.map((hotel) => (
                  <MenuItem key={hotel.hotelId} value={hotel.hotelId}>
                    {hotel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="standard" sx={{ minWidth: 150 }}>
              <InputLabel id="select-permission-label">Permission</InputLabel>
              <Select
                labelId="select-permission-label"
                name="permissionLevel"
                value={currentHotelSelectionForNewUser.permissionLevel}
                onChange={this.handleCurrentHotelSelectionChange}
                label="Permission"
                disabled={!currentHotelSelectionForNewUser.hotelId}
              >
                {!currentHotelSelectionForNewUser.hotelId ? (
                  <MenuItem value="" disabled>
                    <em>Select a hotel first</em>
                  </MenuItem>
                ) : (
                  // Find the selected hotel and show appropriate permission options
                  (() => {
                    const selectedHotel = this.props.hotelData.hotels.find(
                      (hotel) =>
                        hotel.hotelId ===
                        currentHotelSelectionForNewUser.hotelId,
                    );

                    if (selectedHotel && selectedHotel.level === "Pro") {
                      // Return array of MenuItems instead of using Fragment
                      return [
                        <MenuItem key="pro-admin" value={30}>
                          Pro Admin
                        </MenuItem>,
                        <MenuItem key="pro-user" value={10}>
                          Revanista User
                        </MenuItem>,
                        <MenuItem key="restricted-user" value={5}>
                          Restricted User
                        </MenuItem>,
                      ];
                    } else {
                      return [
                        <MenuItem key="restricted-user" value={10}>
                          Revanista User
                        </MenuItem>,
                        <MenuItem key="restricted-user" value={5}>
                          Restricted User
                        </MenuItem>,
                      ];
                    }
                  })()
                )}
              </Select>
            </FormControl>
            <Tooltip
              title={
                currentHotelSelectionForNewUser.notificationsEnabled
                  ? "Disable Notifications"
                  : "Enable Notifications"
              }
            >
              <IconButton
                onClick={this.handleToggleCurrentHotelNotification}
                color="primary"
              >
                {currentHotelSelectionForNewUser.notificationsEnabled ? (
                  <NotificationsIcon />
                ) : (
                  <NotificationsOffIcon />
                )}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={this.handleAddHotelPermissionToDialogList}
              disabled={
                !currentHotelSelectionForNewUser.hotelId ||
                !currentHotelSelectionForNewUser.permissionLevel
              }
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>

          {newHotelUserPermissions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Configured Hotel Access
              </Typography>
              {newHotelUserPermissions.map((perm, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">
                      {perm.hotelName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Permission: {getPermissionName(perm.permissionLevel)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {perm.notificationsEnabled ? (
                      <NotificationsIcon color="action" sx={{ mr: 1 }} />
                    ) : (
                      <NotificationsOffIcon color="action" sx={{ mr: 1 }} />
                    )}
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() =>
                        this.handleRemoveHotelPermissionFromDialogList(index)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseAddHotelUserDialog}>Cancel</Button>
          <Button onClick={this.handleSaveNewHotelUser} variant="contained">
            Save User
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Filter GNH users based on search query
  getFilteredGnhUsers = () => {
    const {
      gnhUsers,
      gnhSearchQuery,
      selectedGnhPermissionFilter,
      selectedGnhHotelFilter,
    } = this.state;
    let filteredUsers = gnhUsers;

    // 1. Filter by permission level
    if (selectedGnhPermissionFilter !== "all") {
      filteredUsers = filteredUsers.filter((user) => {
        if (
          typeof user.permissions === "object" &&
          user.permissions.gnh !== undefined
        ) {
          return user.permissions.gnh === Number(selectedGnhPermissionFilter);
        }
        if (typeof user.permissions === "number") {
          return user.permissions === Number(selectedGnhPermissionFilter);
        }
        return false;
      });
    }

    // 2. Filter by hotel notification
    if (selectedGnhHotelFilter !== "all") {
      filteredUsers = filteredUsers.filter((user) => {
        if (
          typeof user.permissions === "object" &&
          user.permissions.notification &&
          Array.isArray(user.permissions.notification)
        ) {
          return user.permissions.notification.includes(
            Number(selectedGnhHotelFilter),
          );
        }
        return false;
      });
    }

    // 3. Filter by email search query
    if (gnhSearchQuery) {
      const lowercasedQuery = gnhSearchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(lowercasedQuery),
      );
    }

    return filteredUsers;
  };

  // Handler for GNH hotel filter change
  handleGnhHotelFilterChange = (event: SelectChangeEvent<number | "all">) => {
    // Convert string value to number if it's not "all"
    const value =
      event.target.value === "all" ? "all" : Number(event.target.value);
    this.setState({ selectedGnhHotelFilter: value });
  };

  // Render the GNH staff table
  renderGnhStaffTable = () => {
    const { gnhSearchQuery } = this.state;
    const filteredGnhUsers = this.getFilteredGnhUsers();

    let currentUserEmail = this.props.user?.username;
    if (!currentUserEmail) {
      try {
        const userDataString = localStorage.getItem("userData");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          currentUserEmail = userData?.username;
          console.log("Retrieved email from localStorage:", currentUserEmail);
        }
      } catch (error) {
        console.error("Error retrieving user data from localStorage:", error);
      }
    }

    const isSystemAdmin = hasPermission(ACTIONS.MODIFY_GNHSTAFF);

    return (
      <TableContainer
        component={Paper}
        sx={{ marginBottom: 4, width: "100%", margin: "0 auto" }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by email"
            value={gnhSearchQuery}
            onChange={this.handleGnhSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: gnhSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={this.clearGnhSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ width: 250 }}>
            <InputLabel>Permission</InputLabel>
            <Select
              value={this.state.selectedGnhPermissionFilter}
              onChange={this.handleGnhPermissionFilterChange}
              label="Filter by Permission"
            >
              <MenuItem value="all">
                <em>All Permissions</em>
              </MenuItem>
              <MenuItem value={50}>GNH Systems Admins</MenuItem>
              <MenuItem value={40}>GNH Revenue Managers</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ width: 250 }}>
            <InputLabel>Hotel</InputLabel>
            <Select
              value={this.state.selectedGnhHotelFilter}
              onChange={this.handleGnhHotelFilterChange}
              label="Filter by Hotel"
            >
              <MenuItem value="all">
                <em>All Hotels</em>
              </MenuItem>
              {this.props.hotelData.hotels.map((hotel) => (
                <MenuItem key={hotel.hotelId} value={hotel.hotelId}>
                  {hotel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isSystemAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openAddGnhUserDialog}
              sx={{ whiteSpace: "nowrap", minWidth: "120px" }}
            >
              New User
            </Button>
          )}
        </Box>
        <Table
          sx={{ minWidth: 650, width: "100%" }}
          aria-label="GNH staff table"
        >
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Permission</TableCell>
              <TableCell align="right" style={{ paddingRight: "40px" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGnhUsers && filteredGnhUsers.length > 0 ? (
              filteredGnhUsers.map((staffUser) => (
                <React.Fragment key={staffUser.email}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {staffUser.email}
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography>
                          {this.getPermissionText(staffUser.permissions)}
                        </Typography>
                        {staffUser.permissions.notification &&
                          staffUser.permissions.notification.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Notifications:{" "}
                              {staffUser.permissions.notification.length === 1
                                ? this.props.hotelData.hotels.find(
                                    (h) =>
                                      h.hotelId ===
                                      staffUser.permissions.notification[0],
                                  )?.name || "1 hotel"
                                : `${staffUser.permissions.notification.length} hotels`}
                            </Typography>
                          )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {!staffUser.verificationStatus && (
                        <>
                          <Tooltip title="User had not completed first log in">
                            <IconButton size="small" color="warning">
                              <WarningIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              onClick={() =>
                                this.handleGNHPasswordReset(staffUser)
                              }
                              size="small"
                              color="primary"
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* Edit button - shown to system admins for all users, and to regular users for their own account (notifications only) */}
                      <Tooltip title="Edit User">
                        <IconButton
                          onClick={() => this.handleEditUser(staffUser)}
                          size="small"
                          color="primary"
                        >
                          {this.state.expandedUser === staffUser.email ? (
                            <EditOffIcon />
                          ) : (
                            <EditIcon />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Delete button - only shown to system admins and not for their own account */}
                      {isSystemAdmin &&
                        staffUser.email.toLowerCase() !==
                          currentUserEmail?.toLowerCase() && (
                          <Tooltip title="Delete User">
                            <IconButton
                              onClick={() =>
                                this.handleDeleteUser(staffUser.email)
                              }
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={3}
                    >
                      <Collapse
                        in={this.state.expandedUser === staffUser.email}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            {staffUser.email.toLowerCase() ===
                            currentUserEmail?.toLowerCase()
                              ? "Edit Notification Settings"
                              : "Edit User Permission"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              marginTop: 2,
                            }}
                          >
                            {/* Only show permission selection if user is not editing their own account or is a system admin */}
                            {isSystemAdmin &&
                              staffUser.email.toLowerCase() !==
                                currentUserEmail?.toLowerCase() && (
                                <FormControl fullWidth>
                                  <InputLabel id="permission-select-label">
                                    Permission
                                  </InputLabel>
                                  <Select
                                    labelId="permission-select-label"
                                    value={this.state.editingPermission || ""}
                                    label="Permission"
                                    onChange={this.handlePermissionChange}
                                  >
                                    <MenuItem value={50}>
                                      <Tooltip
                                        title="Systems team at GNh with full access to all configuration & features"
                                        placement="right"
                                        arrow
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "block",
                                            width: "100%",
                                          }}
                                        >
                                          GNH Systems
                                        </Box>
                                      </Tooltip>
                                    </MenuItem>
                                    <MenuItem value={40}>
                                      <Tooltip
                                        title="Revenue managers at GNH with access to certain configuration & all features"
                                        placement="right"
                                        arrow
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "block",
                                            width: "100%",
                                          }}
                                        >
                                          GNH Revenue Managers
                                        </Box>
                                      </Tooltip>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            {/* If user is editing their own account and is not a system admin, show their permission level as read-only */}
                            {staffUser.email.toLowerCase() ===
                              currentUserEmail?.toLowerCase() &&
                              !isSystemAdmin && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Permission Level
                                  </Typography>
                                  <Typography variant="body1">
                                    {this.getPermissionText(
                                      staffUser.permissions,
                                    )}
                                  </Typography>
                                </Box>
                              )}

                            <Typography variant="subtitle1" sx={{ mt: 1 }}>
                              Hotels for Notifications
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item sm={12}>
                                <FormControl variant="outlined" fullWidth>
                                  <InputLabel id="mutiple-hotel-select-label">
                                    Select hotels for notifications
                                  </InputLabel>
                                  <Select
                                    labelId="mutiple-hotel-select-label"
                                    label="Select hotels for notifications"
                                    name="selectHotels"
                                    multiple
                                    value={this.state.editingNotifications}
                                    onChange={(event) => {
                                      const value = event.target
                                        .value as number[];
                                      this.setState({
                                        editingNotifications: value,
                                      });
                                    }}
                                    renderValue={(selected: unknown) => (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        {(selected as number[]).map(
                                          (hotelId) => {
                                            const hotel =
                                              this.props.hotelData.hotels.find(
                                                (h) => h.hotelId === hotelId,
                                              );
                                            return (
                                              <Chip
                                                key={hotelId}
                                                label={
                                                  hotel ? hotel.name : hotelId
                                                }
                                              />
                                            );
                                          },
                                        )}
                                      </Box>
                                    )}
                                  >
                                    <MenuItem value="all">
                                      <ListItemIcon>
                                        <Checkbox
                                          checked={
                                            this.state.editingNotifications
                                              .length ===
                                            this.props.hotelData.hotels.length
                                          }
                                          indeterminate={
                                            this.state.editingNotifications
                                              .length > 0 &&
                                            this.state.editingNotifications
                                              .length <
                                              this.props.hotelData.hotels.length
                                          }
                                          onChange={() => {
                                            if (
                                              this.state.editingNotifications
                                                .length ===
                                              this.props.hotelData.hotels.length
                                            ) {
                                              // Deselect all
                                              this.setState({
                                                editingNotifications: [],
                                              });
                                            } else {
                                              // Select all
                                              this.setState({
                                                editingNotifications:
                                                  this.props.hotelData.hotels.map(
                                                    (h) => h.hotelId,
                                                  ),
                                              });
                                            }
                                          }}
                                        />
                                      </ListItemIcon>
                                      <ListItemText primary="Select All" />
                                    </MenuItem>
                                    {this.props.hotelData.hotels.map(
                                      (hotel) => (
                                        <MenuItem
                                          key={hotel.hotelId}
                                          value={hotel.hotelId}
                                        >
                                          <ListItemIcon>
                                            <Checkbox
                                              checked={
                                                this.state.editingNotifications.indexOf(
                                                  hotel.hotelId,
                                                ) > -1
                                              }
                                            />
                                          </ListItemIcon>
                                          <ListItemText primary={hotel.name} />
                                        </MenuItem>
                                      ),
                                    )}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 2,
                              }}
                            >
                              <Button
                                variant="contained"
                                startIcon={<CancelIcon />}
                                onClick={this.handleCancelEdit}
                                style={{
                                  marginRight: "10px",
                                  backgroundColor: "#808080",
                                  color: "#ffffff",
                                  cursor: "pointer",
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={() =>
                                  this.handleSavePermission(staffUser.email)
                                }
                                style={{
                                  backgroundColor: "#ce0e29",
                                  color: "#ffffff",
                                  marginRight: "10px",
                                  cursor: "pointer",
                                }}
                              >
                                Save
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  renderScheduleUpdateDialog = () => {
    const {
      isScheduleUpdateDialogOpen,
      scheduleUpdateEpochTime,
      scheduleUpdateError,
    } = this.state;

    return (
      <Dialog
        open={isScheduleUpdateDialogOpen}
        onClose={this.handleCloseScheduleUpdateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Maintenance Update</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the epoch time for when the maintenance notification should be
            sent. You can use{" "}
            <a
              href="https://www.epochconverter.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#ce0e29" }}
            >
              epochconverter.com
            </a>{" "}
            to convert your desired date and time to epoch format.
          </DialogContentText>
          {scheduleUpdateError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {scheduleUpdateError}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Epoch Time"
            type="number"
            fullWidth
            variant="standard"
            value={scheduleUpdateEpochTime}
            onChange={this.handleScheduleUpdateInputChange}
            placeholder="e.g., 1691395200"
            helperText="Enter the Unix timestamp (epoch time) for the maintenance window"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseScheduleUpdateDialog}>Cancel</Button>
          <Button onClick={this.handleScheduleUpdate} variant="contained">
            Schedule Update
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderConfirmDeleteDialog = () => {
    const { isConfirmDeleteDialogOpen, userToDelete } = this.state;

    return (
      <Dialog
        open={isConfirmDeleteDialogOpen}
        onClose={this.handleCloseConfirmDeleteDialog}
        aria-labelledby="confirm-delete-dialog-title"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete the user:
            <strong> {userToDelete}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseConfirmDeleteDialog}>Cancel</Button>
          <Button
            onClick={this.handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Filter Hotel users based on selected hotel and email search
  getFilteredHotelUsers = (): hotelStaff[] => {
    const { hotelUsers, hotelSearchQuery, selectedHotelFilter } = this.state;
    let filteredUsers = hotelUsers;

    // 1. Filter by selected hotel
    if (selectedHotelFilter !== "all") {
      filteredUsers = filteredUsers.filter((user) =>
        user.permissions.some(
          (permission) => permission.hotelId === Number(selectedHotelFilter),
        ),
      );
    }

    // 2. Filter by email search query
    if (hotelSearchQuery) {
      const lowercasedQuery = hotelSearchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(lowercasedQuery),
      );
    }

    return filteredUsers;
  };

  renderHotelStaffTable = () => {
    const { hotelSearchQuery } = this.state;
    const filteredHotelUsers = this.getFilteredHotelUsers();

    let currentUserEmail = this.props.user?.username;
    if (!currentUserEmail) {
      try {
        const userDataString = localStorage.getItem("userData");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          currentUserEmail = userData?.email;
          console.log("Retrieved email from localStorage:", currentUserEmail);
        }
      } catch (error) {
        console.error("Error retrieving user data from localStorage:", error);
      }
    }

    const isSystemAdmin = hasPermission(ACTIONS.MODIFY_HOTELSTAFF);

    return (
      <TableContainer
        component={Paper}
        sx={{ marginBottom: 4, width: "100%", margin: "0 auto" }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by email"
            value={hotelSearchQuery}
            onChange={this.handleHotelSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: hotelSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={this.clearHotelSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ width: 250 }}>
            <InputLabel>Filter by Hotel</InputLabel>
            <Select
              value={this.state.selectedHotelFilter}
              onChange={this.handleHotelFilterChange}
              label="Filter by Hotel"
            >
              <MenuItem value="all">
                <em>All Hotels</em>
              </MenuItem>
              {this.props.hotelData.hotels.map((hotel) => (
                <MenuItem key={hotel.hotelId} value={hotel.hotelId}>
                  {hotel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isSystemAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleOpenAddHotelUserDialog} // Correct handler for hotel user
              sx={{ whiteSpace: "nowrap", minWidth: "120px", ml: 1 }}
            >
              New User
            </Button>
          )}
        </Box>
        <Table sx={{ minWidth: 650 }} aria-label="Hotel staff table">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Hotels</TableCell>
              <TableCell align="right" style={{ paddingRight: "40px" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHotelUsers && filteredHotelUsers.length > 0 ? (
              filteredHotelUsers.map((hotelUser) => (
                <React.Fragment key={hotelUser.email}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {hotelUser.email}
                    </TableCell>

                    <TableCell>
                      {hotelUser.permissions.length === 1
                        ? this.props.hotelData.hotels.find(
                            (hotel) =>
                              hotel.hotelId ===
                              hotelUser.permissions[0].hotelId,
                          )?.name || "Unknown Hotel"
                        : `${hotelUser.permissions.length} hotels`}
                    </TableCell>
                    <TableCell align="right">
                      {!hotelUser.verificationStatus && (
                        <>
                          <Tooltip title="User had not completed first log in">
                            <IconButton size="small" color="warning">
                              <WarningIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              onClick={() =>
                                this.handleHotelPasswordReset(hotelUser)
                              }
                              size="small"
                              color="primary"
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* Edit button - shown to system admins for all users, and to regular users for their own account (notifications only) */}
                      <Tooltip title="Edit User">
                        <IconButton
                          onClick={() => this.handleEditHotelUser(hotelUser)}
                          size="small"
                          color="primary"
                        >
                          {this.state.expandedHotelUser === hotelUser.email ? (
                            <EditOffIcon />
                          ) : (
                            <EditIcon />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Delete button - only shown to system admins and not for their own account */}
                      {isSystemAdmin &&
                        hotelUser.email.toLowerCase() !==
                          currentUserEmail?.toLowerCase() && (
                          <Tooltip title="Delete User">
                            <IconButton
                              onClick={() =>
                                this.handleDeleteUser(hotelUser.email)
                              }
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={3}
                    >
                      <Collapse
                        in={this.state.expandedHotelUser === hotelUser.email}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Edit Hotel Permissions
                          </Typography>

                          {/* List current hotels with remove button */}
                          {this.state.editingHotelPermissions &&
                            Object.keys(this.state.editingHotelPermissions).map(
                              (hotelIdStr) => {
                                const hotelId = parseInt(hotelIdStr, 10);
                                const currentPermission =
                                  this.state.editingHotelPermissions?.[hotelId];
                                const notificationEnabled =
                                  this.state.editingHotelNotifications?.[
                                    hotelId
                                  ] ?? false;

                                return (
                                  <Box
                                    key={hotelId}
                                    sx={{
                                      mb: 3,
                                      pb: 2,
                                      borderBottom: "1px solid #eee",
                                    }}
                                  >
                                    <Typography variant="subtitle1">
                                      {this.getHotelNameById(hotelId)}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mt: 1,
                                      }}
                                    >
                                      <FormControl sx={{ minWidth: 200 }}>
                                        <InputLabel>Permission</InputLabel>
                                        <Select
                                          value={currentPermission || ""}
                                          label="Permission"
                                          onChange={(e) =>
                                            this.handleHotelPermissionChange(
                                              hotelId,
                                              e.target.value as number,
                                            )
                                          }
                                        >
                                          {(() => {
                                            const selectedHotel =
                                              this.props.hotelData.hotels.find(
                                                (hotel) =>
                                                  hotel.hotelId === hotelId,
                                              );

                                            if (
                                              selectedHotel &&
                                              selectedHotel.level === "Pro"
                                            ) {
                                              return [
                                                <MenuItem
                                                  key="pro-admin"
                                                  value={30}
                                                >
                                                  Pro Admin
                                                </MenuItem>,
                                                <MenuItem
                                                  key="pro-user"
                                                  value={10}
                                                >
                                                  Revanista User
                                                </MenuItem>,
                                                <MenuItem
                                                  key="restricted-user"
                                                  value={5}
                                                >
                                                  Restricted User
                                                </MenuItem>,
                                              ];
                                            } else {
                                              return [
                                                <MenuItem value={10}>
                                                  Revanista User
                                                </MenuItem>,
                                                <MenuItem
                                                  key="restricted-user"
                                                  value={5}
                                                >
                                                  Restricted User
                                                </MenuItem>,
                                              ];
                                            }
                                          })()}
                                        </Select>
                                      </FormControl>
                                      <Tooltip
                                        title={
                                          notificationEnabled
                                            ? "Disable Notifications"
                                            : "Enable Notifications"
                                        }
                                      >
                                        <IconButton
                                          onClick={() =>
                                            this.toggleHotelNotification(
                                              hotelId,
                                            )
                                          }
                                          color={
                                            notificationEnabled
                                              ? "primary"
                                              : "default"
                                          }
                                        >
                                          {notificationEnabled ? (
                                            <NotificationsIcon />
                                          ) : (
                                            <NotificationsOffIcon />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Remove Hotel Access">
                                        <IconButton
                                          onClick={() =>
                                            this.handleRemoveHotel(hotelId)
                                          }
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                );
                              },
                            )}

                          {/* Add new hotel section */}
                          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                            Add Hotel Access
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                            }}
                          >
                            <FormControl sx={{ minWidth: 220 }}>
                              <InputLabel>Select Hotel</InputLabel>
                              <Select
                                name="newHotelIdToAdd"
                                value={this.state.newHotelIdToAdd}
                                onChange={this.handleHotelSelectChange}
                                label="Select Hotel"
                              >
                                {this.props.hotelData.hotels
                                  .filter(
                                    (hotel) =>
                                      !this.state.editingHotelPermissions ||
                                      !this.state.editingHotelPermissions[
                                        hotel.hotelId
                                      ],
                                  )
                                  .map((hotel) => (
                                    <MenuItem
                                      key={hotel.hotelId}
                                      value={hotel.hotelId}
                                    >
                                      {hotel.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 200 }}>
                              <InputLabel>Permission</InputLabel>
                              <Select
                                name="newHotelPermissionToAdd"
                                value={this.state.newHotelPermissionToAdd}
                                onChange={this.handleHotelSelectChange}
                                label="Permission"
                                disabled={!this.state.newHotelIdToAdd}
                              >
                                {!this.state.newHotelIdToAdd ? (
                                  <MenuItem value="" disabled>
                                    <em>Select a hotel first</em>
                                  </MenuItem>
                                ) : (
                                  (() => {
                                    const selectedHotel =
                                      this.props.hotelData.hotels.find(
                                        (hotel) =>
                                          hotel.hotelId ===
                                          this.state.newHotelIdToAdd,
                                      );

                                    if (
                                      selectedHotel &&
                                      selectedHotel.level === "Pro"
                                    ) {
                                      return [
                                        <MenuItem key="pro-admin" value={30}>
                                          Pro Admin
                                        </MenuItem>,
                                        <MenuItem key="pro-user" value={10}>
                                          Revanista User
                                        </MenuItem>,
                                        <MenuItem
                                          key="restricted-user"
                                          value={5}
                                        >
                                          Restricted User
                                        </MenuItem>,
                                      ];
                                    } else {
                                      return [
                                        <MenuItem value={10}>
                                          Revanista User
                                        </MenuItem>,
                                        <MenuItem
                                          key="restricted-user"
                                          value={5}
                                        >
                                          Restricted User
                                        </MenuItem>,
                                      ];
                                    }
                                  })()
                                )}
                              </Select>
                            </FormControl>
                            <Button
                              variant="contained"
                              onClick={this.handleAddHotel}
                              disabled={!this.state.newHotelIdToAdd}
                            >
                              Add
                            </Button>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 4,
                            }}
                          >
                            <Button
                              variant="contained"
                              startIcon={<CancelIcon />}
                              onClick={this.handleCancelEdit}
                              style={{
                                marginRight: "10px",
                                backgroundColor: "#808080",
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() =>
                                this.handleSaveHotelPermissions(hotelUser.email)
                              }
                              style={{ backgroundColor: "#ce0e29" }}
                            >
                              Save Changes
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  render() {
    const isDeveloper = hasPermission(ACTIONS.DEVELOPER);

    return (
      <ThemeProvider theme={baseTheme}>
        {/* Developer Settings Section - Only show for developers */}
        {isDeveloper && (
          <div className="content">
            <Box sx={{ width: "75%", margin: "0 auto", mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  mb: 2,
                }}
              >
                <SectionTitle {...{ value: "Developer Settings" }} />
              </Box>
              <Paper sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  System maintenance and administrative tools for developers.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={this.handleOpenScheduleUpdateDialog}
                    sx={{
                      backgroundColor: "#ce0e29",
                      "&:hover": {
                        backgroundColor: "#a00b22",
                      },
                    }}
                  >
                    Schedule Update
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={this.handleOpenDeleteHotelDialog}
                  >
                    Delete Hotel
                  </Button>
                </Box>
              </Paper>
            </Box>
          </div>
        )}

        {/* GNH Staff Section */}
        <div className="content">
          <Box sx={{ width: "75%", margin: "0 auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <SectionTitle {...{ value: "GNH Staff" }} />
            </Box>
            {this.renderGnhStaffTable()}
          </Box>
        </div>
        <div className="content" style={{ paddingBottom: "50px" }}>
          <Box sx={{ width: "75%", margin: "0 auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <SectionTitle {...{ value: "Hotel Staff" }} />
            </Box>
            {this.renderHotelStaffTable()}
          </Box>
        </div>
        {this.renderAddGnhUserDialog()}
        {this.renderAddHotelUserDialog()}
        {this.renderConfirmDeleteDialog()}
        {this.renderScheduleUpdateDialog()}
        {this.renderDeleteHotelDialog()}
        {this.renderConfirmDeleteHotelDialog()}
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    hotelData: state.hotelData,
  };
};

const UserDashboardWithRouter = (props: Omit<Props, "router">) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const router = { navigate, location, params };
  return <UserDashboard {...props} router={router} />;
};

export default connect(mapStateToProps)(UserDashboardWithRouter);
